(() => {
  const selector = '[data-monoree-delivery-estimate]';

  const weekdayLabels = {
    en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    lt: ['sekmadienį', 'pirmadienį', 'antradienį', 'trečiadienį', 'ketvirtadienį', 'penktadienį', 'šeštadienį'],
    lv: ['svētdien', 'pirmdien', 'otrdien', 'trešdien', 'ceturtdien', 'piektdien', 'sestdien'],
    et: ['pühapäev', 'esmaspäev', 'teisipäev', 'kolmapäev', 'neljapäev', 'reede', 'laupäev'],
    pl: ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota'],
  };

  const summaryWeekdayLabels = {
    en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    lt: ['sekmadienio', 'pirmadienio', 'antradienio', 'trečiadienio', 'ketvirtadienio', 'penktadienio', 'šeštadienio'],
    lv: ['svētdienas', 'pirmdienas', 'otrdienas', 'trešdienas', 'ceturtdienas', 'piektdienas', 'sestdienas'],
    et: ['pühapäevast', 'esmaspäevast', 'teisipäevast', 'kolmapäevast', 'neljapäevast', 'reedest', 'laupäevast'],
    pl: ['niedzieli', 'poniedziałku', 'wtorku', 'środy', 'czwartku', 'piątku', 'soboty'],
  };

  const summaryPrefixes = {
    en: 'from',
    lt: 'nuo',
    lv: 'no',
    et: 'alates',
    pl: 'od',
  };

  const marketDate = (timeZone) => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());

      const values = {};
      parts.forEach(({ type, value }) => {
        values[type] = value;
      });

      return new Date(
        Number(values.year),
        Number(values.month) - 1,
        Number(values.day),
        12,
        0,
        0,
        0
      );
    } catch (error) {
      const fallback = new Date();
      fallback.setHours(12, 0, 0, 0);
      return fallback;
    }
  };

  const addDeliveryDays = (startDate, amount) => {
    const date = new Date(startDate);
    let counted = 0;

    while (counted < amount) {
      date.setDate(date.getDate() + 1);

      // Sunday is excluded. Saturday can be a carrier day.
      if (date.getDay() === 0) continue;

      counted += 1;
    }

    return date;
  };

  const softenSaturdayBoundary = (date) => {
    const adjusted = new Date(date);

    // Saturday delivery is possible, but it is not promised as a range edge.
    if (adjusted.getDay() === 6) {
      adjusted.setDate(adjusted.getDate() + 2);
    }

    return adjusted;
  };

  const localeLanguage = (locale) => {
    const language = String(locale || 'en').split('-')[0].toLowerCase();
    return weekdayLabels[language] ? language : 'en';
  };

  const formatWeekdayRange = (start, end, locale) => {
    const labels = weekdayLabels[localeLanguage(locale)];
    const first = labels[start.getDay()];
    const last = labels[end.getDay()];

    return start.getTime() === end.getTime() ? first : `${first}–${last}`;
  };

  const formatDateRange = (start, end, locale, today) => {
    const resolvedLocale = locale || 'en-GB';
    const isLithuanian = /^lt(?:-|$)/i.test(resolvedLocale);
    const includeYear =
      start.getFullYear() !== end.getFullYear() ||
      end.getFullYear() !== today.getFullYear();

    // Lithuanian's short month format is numeric (for example, 10-19),
    // which is ambiguous in a delivery estimate. Use the full month name.
    const formatter = new Intl.DateTimeFormat(resolvedLocale, {
      day: 'numeric',
      month: isLithuanian ? 'long' : 'short',
      ...(includeYear ? { year: 'numeric' } : {}),
    });

    let formatted;

    if (start.getTime() === end.getTime()) {
      formatted = formatter.format(start);
    } else if (typeof formatter.formatRange === 'function') {
      formatted = formatter.formatRange(start, end);
    } else {
      formatted = `${formatter.format(start)}–${formatter.format(end)}`;
    }

    // Some ICU versions omit the Lithuanian day suffix for date ranges.
    if (isLithuanian && start.getTime() !== end.getTime() && !/d\.$/i.test(formatted)) {
      formatted = `${formatted} d.`;
    }

    return formatted;
  };

  const formatSummaryDate = (date, locale, today) => {
    const resolvedLocale = locale || 'en-GB';
    const language = localeLanguage(resolvedLocale);
    const includeYear = date.getFullYear() !== today.getFullYear();
    const formatter = new Intl.DateTimeFormat(resolvedLocale, {
      day: 'numeric',
      month: language === 'lt' ? 'long' : 'short',
      ...(includeYear ? { year: 'numeric' } : {}),
    });

    let formatted = formatter.format(date).trim();

    // Keep the Lithuanian date grammatically complete in the collapsed row.
    if (language === 'lt' && !/\sd\.$/i.test(formatted)) {
      formatted = `${formatted} d.`;
    }

    return formatted;
  };

  const formatSummaryEstimate = (date, locale, today) => {
    const language = localeLanguage(locale);
    const prefix = summaryPrefixes[language];
    const weekday = summaryWeekdayLabels[language][date.getDay()];
    const formattedDate = formatSummaryDate(date, locale, today);

    return `${prefix} ${weekday}, ${formattedDate}`;
  };

  const capitalizeFirst = (value, locale) => {
    const text = String(value || '');
    if (!text) return text;

    return text.charAt(0).toLocaleUpperCase(locale || undefined) + text.slice(1);
  };

  const detailedEstimate = (locale, weekdays, dates) =>
    `${capitalizeFirst(weekdays, locale)} (${dates})`;

  const update = (element) => {
    const minDays = Number.parseInt(element.dataset.minDays || '1', 10);
    const maxDays = Number.parseInt(element.dataset.maxDays || '3', 10);
    const locale = element.dataset.locale || 'en-GB';
    const timeZone = element.dataset.timezone || 'Europe/Vilnius';

    if (
      !Number.isFinite(minDays) ||
      !Number.isFinite(maxDays) ||
      minDays < 0 ||
      maxDays < minDays
    ) {
      return;
    }

    const today = marketDate(timeZone);
    let earliest = softenSaturdayBoundary(addDeliveryDays(today, minDays));
    const latest = softenSaturdayBoundary(addDeliveryDays(today, maxDays));

    if (earliest.getTime() > latest.getTime()) {
      earliest = new Date(latest);
    }

    const weekdays = formatWeekdayRange(earliest, latest, locale);
    const dates = formatDateRange(earliest, latest, locale, today);
    const isSummary = Boolean(element.closest('summary'));

    element.textContent = isSummary
      ? formatSummaryEstimate(earliest, locale, today)
      : detailedEstimate(locale, weekdays, dates);
    element.dataset.resolved = 'true';
  };

  const refresh = (root = document) => {
    if (root.matches?.(selector)) update(root);
    root.querySelectorAll?.(selector).forEach(update);
  };

  if (window.MonoreeDeliveryEstimate?.refresh) {
    window.MonoreeDeliveryEstimate.refresh(document);
    return;
  }

  window.MonoreeDeliveryEstimate = { refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true });
  } else {
    refresh(document);
  }

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
})();
